/**
 * Sync progress (`GET /v1/shell/sync-progress`) for the offices in scope.
 *
 * Polling: every 12 s while the popover or the Sync status page is open,
 * every 30 s while something is syncing (the chip ring moves), otherwise
 * every 5 minutes so the ring appears when a cycle or backfill starts
 * (it was once a minute per open tab; 2026-10-17 perf fix).
 */
import { useQuery } from '@tanstack/react-query';
import { getShellSyncProgress } from '@velora/sdk';
import { clientFor } from '../api.js';
import { useOfficeScope } from '../office/OfficeScope.js';

export const SYNC_PROGRESS_KEY = ['shell', 'sync-progress'] as const;
export const OPEN_POLL_MS = 12_000;
export const ACTIVE_POLL_MS = 30_000;
export const IDLE_POLL_MS = 5 * 60_000;

export function useSyncProgress({ open = false }: { open?: boolean } = {}) {
  const { param, locationIds } = useOfficeScope();
  return useQuery({
    // Polling only, no live entity keys: sync_run, sync_link_state and
    // imaging_source change every few seconds while anything syncs, and each
    // change refetched this route from every open tab (2026-10-17).
    queryKey: [...SYNC_PROGRESS_KEY, { office: param }],
    queryFn: ({ signal }) => getShellSyncProgress(clientFor(param), { signal }),
    refetchInterval: (q) =>
      open ? OPEN_POLL_MS : q.state.data?.active ? ACTIVE_POLL_MS : IDLE_POLL_MS,
    refetchIntervalInBackground: false,
    enabled: locationIds.length > 0,
  });
}
